import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isError, isLoading, Status } from '../domain/status';
import { SearchInputControl } from '../components/chat-controls/SeachInputControl';
import { MultilineSearchInputControl } from '../components/chat-controls/MultilineSeachInputControl';
import { ChatConversation } from '../components/chat-controls/ChatConversation';
import { useChatHistory } from './useChatHistory';
import { track, HEAP_SCREENS, HEAP_EVENTS } from '../utils/analytics';
import { Settings, SettingsProvider } from '../components/context/SettingsContext';
import {
    getGreeting,
    getLocationSuffix,
    INITIAL_STATE,
    onlyUserQueries,
} from './ConciergeScreen.utils';
import { ChatWindow } from '../components/chat-window/ChatWindow';
import { MinimalNLSearchRequest } from '../models';
import { useAsk } from '../hooks/useAsk';
import { ChatbotOverlay } from '../components/overlay/ChatbotOverlay';
import styles from './ConciergeScreen.module.scss';

interface ConciergeScreenProps {
    /**
     * onClose
     *
     * Callback to close the chatbot overlay.
     * The rendering context should determine when the ChatOverlay is visible.
     */
    onClose: () => void;

    /**
     * isAuthenticated
     *
     * Boolean indicating if the user is currently authenticated.
     */
    user?: {
        isAuthenticated?: boolean;
        isStaff?: boolean;
        publicId?: string;
    };

    /**
     * location
     *
     * A string containing the user's current known location.
     *
     * This is used as a default location for events.
     */
    location?: {
        placeId: string;
        placeType: string;
        latitude: number;
        longitude: number;
        currentPlace: string;
        currentPlaceParent: string;
        slug: string;
        isOnline?: boolean;
    };

    /**
     * Custom settings object used primarily by the developer sandbox.
     */
    settings?: Settings;
}

export const ConciergeScreen = (props: ConciergeScreenProps) => {
    const [messagesSent, setMessageSent] = useState(0);

    // Keep a ref to the chat container for scrolling
    const chatContainerRef = useRef<HTMLDivElement>(null);

    // Ref to track if we've already fired the initial load event
    const hasTrackedInitialLoad = useRef(false);

    const [pendingRequest, setPendingRequest] = useState<MinimalNLSearchRequest>(INITIAL_STATE);
    const { data, status: requestStatus } = useAsk(pendingRequest);

    const chatHistoryContext = useChatHistory();
    const { dispatchQuery, dispatchResponse, dispatchStatus, dispatchReset } = chatHistoryContext;
    const { messages, status } = chatHistoryContext;

    // FIXME: What is this for?
    const [focusIndex] = useState(0);

    /**
     * Effect that runs only when the component first loads
     */
    useEffect(() => {
        // Track component load only once
        if (!hasTrackedInitialLoad.current) {
            track({
                eventName: HEAP_EVENTS.ScreenView,
                eventData: {
                    Screen: HEAP_SCREENS.CONCIERGE,
                },
            });
            hasTrackedInitialLoad.current = true;
        }
    }, []);

    /**
     * Update the status and messages based on the NLWeb response
     */
    useEffect(() => {
        if (isLoading(requestStatus) && !isLoading(status)) {
            dispatchStatus(Status.Loading);
        } else if (!isLoading(requestStatus) && isLoading(status) && data !== null) {
            dispatchResponse(data as any);
        }
    }, [data, status, messages, dispatchResponse, dispatchStatus, requestStatus]);

    /**
     * Update the list of messages to include the new query
     */
    const onSubmit = useCallback(
        async (query: string) => {
            const normalizedQuery = query.trim();
            if (!normalizedQuery) {
                return;
            }
            setMessageSent((count) => count + 1);

            if (pendingRequest.query === normalizedQuery) {
                return;
            }

            track({
                eventName: HEAP_EVENTS.ChatMessageSent,
                eventData: {
                    Screen: HEAP_SCREENS.CONCIERGE,
                    query: normalizedQuery,
                    messagesSent: messagesSent,
                },
            });

            const newRequest: MinimalNLSearchRequest = {
                location: props.location?.slug,
                query: normalizedQuery,
                queryHistory: onlyUserQueries(messages),
            };

            setPendingRequest(newRequest);
            dispatchQuery(newRequest);
        },
        [messages, pendingRequest, dispatchQuery, props.location, messagesSent],
    );

    /**
     * Scroll to the bottom of the chat container when the status is changed to loading
     */
    useEffect(() => {
        if (status !== Status.Loading) {
            return;
        }

        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [status]);

    const handleClose = useCallback(() => {
        dispatchReset();
        props.onClose();
    }, [props.onClose]);

    const currentPlace = props.location?.currentPlace;
    const greeting = useMemo(() => getGreeting(currentPlace), [currentPlace]);
    const optionalSuffix = useMemo(() => getLocationSuffix(currentPlace), [currentPlace]);
    const isLoggedIn = !!props.user?.isAuthenticated;

    return (
        <SettingsProvider initialSettings={props.settings}>
            <ChatbotOverlay>
                <ChatWindow
                    onClose={handleClose}
                    isLoggedIn={isLoggedIn}
                    messagesSent={messagesSent}
                    containerRef={chatContainerRef}
                >
                    <h1 className={styles.tagline} dangerouslySetInnerHTML={{ __html: greeting }} />
                    {messages.length === 0 && (
                        <MultilineSearchInputControl
                            onSubmit={onSubmit}
                            extKey={focusIndex}
                            placeholder={`I want to see a comedy show ${optionalSuffix}`}
                        />
                    )}
                    <ChatConversation
                        messages={messages}
                        requestStatus={requestStatus}
                        messagesSent={messagesSent}
                        onReload={() => {
                            const retry = (pendingRequest.retry || 0) + 1;
                            setPendingRequest({ ...pendingRequest, retry });
                            dispatchQuery({ ...pendingRequest, retry });
                        }}
                    />
                    {messages.length > 0 && (
                        <div className={styles.searchInputContainer}>
                            <SearchInputControl
                                onSubmit={onSubmit}
                                onBack={() => {}}
                                status={'post-search'}
                                extKey={focusIndex}
                                placeholder="Ask another question or share more"
                            />
                        </div>
                    )}
                </ChatWindow>
            </ChatbotOverlay>
        </SettingsProvider>
    );
};
